import { ProductAlert, Product } from 'types';
import * as React from 'react';
import ReactTable, {
  Column,
  ReactTableDefaults,
  SortingRule,
} from 'react-table';
import TableLoader from 'components/Loader/TableLoader';
import './AlertList.scss';
import { DEFAULT_PAGE_SIZE } from 'views/Alerts';
import {
  datePropertyAccessor,
  DropdownFilter,
  NoDataMessage,
  SearchFilter,
  TooltipCell,
} from '../../helpers/Table';
import {
  AlertSortSpecification,
  AlertSortAllowedKey,
  alertSortAllowedKeys,
} from 'services/ProductAlertService';
import Tooltip from '../../components/Tooltip';

export type AlertListProps = {
  alerts: ReadonlyArray<ProductAlert> | null;
  isLoading: boolean;
  numPages: number;
  pageSize: number;
  currentPage: number;
  setPage: (newPageIndex: number) => void;
  setPageSize: (newPageSize: number) => void;
  setSortSpecification: (newSpecification: AlertSortSpecification) => void;
  /** A function to call when an alert needs to be displayed. */
  showAlert: (alert: ProductAlert) => void;
  handleTableFiltersChanged: (filters: { id: string; value: any }[]) => void;
};

const statusTextOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function generateStatusAccessor() {
  return {
    id: 'active',
    accessor(row: ProductAlert) {
      return row.isArchived ? 'Archived' : (row.active ? 'Active' : 'Inactive');
    }
  };
}

const tableColumnSpec: Column[] = [
  {
    Header: 'Alert #',
    accessor: 'alertNumber',
  },
  {
    Header: 'Status',
    ...generateStatusAccessor(),
    filterable: true,
    Filter: ({ onChange, filter }) =>
      DropdownFilter({ onChange, filter, options: statusTextOptions }),
    Cell(row: any) {
      const { original } = row;
      const value = original.isArchived ? 'Archived' : (original.active ? 'Active' : 'Inactive');
      return (
        <Tooltip text={value} showOnlyOnOverflow={true}>
          <span className={value}>
            {value}
          </span>
        </Tooltip>
      );
    },
    width: 200
  },
  {
    Header: 'Date Submitted',
    ...datePropertyAccessor('created'),
  },
  {
    Header: 'Items',
    accessor: 'itemInfo',
    /* This functionality isn't available on the backend and would also have the question of how it is
        interpreted. Would it sort by item name (more difficult to implement) or its number, or just
        whatever text shows up in the field? */
    sortable: false,
    Cell(row: { value: ProductAlert['itemInfo'] }) {
      function cellValue(item: Product): string {
        let value = item.itemNumber;
        return item.itemName !== null ? value + ` - ${item.itemName}` : value;
      }

      return (
        <>
          {row.value.map((item, i) => {
            return (
              <p className="cell-entry" key={i}>
                <TooltipCell value={cellValue(item)} />
              </p>
            );
          })}
        </>
      );
    },
  },
  {
    Header: 'Additional Info',
    accessor: 'additionalInfo',
    sortable: false,
    Filter: SearchFilter,
    filterMethod: (filter, row) => {
      return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
    }
  }
  // {
  //   Header: 'Alert Expiry Date',
  //   ...datePropertyAccessor('expiryDate'),
  // },
];

export default class AlertList extends React.Component<AlertListProps> {
  constructor(props: AlertListProps) {
    super(props);

    this.getTdProps = this.getTdProps.bind(this);
    this.NoDataComponent = this.NoDataComponent.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
  }

  render() {
    const data = [...(this.props.alerts || [])];
    /* Note: We don't quite handle server side data the way it's specified in the React Table
        documentation. Instead of subscribing to `onFetchData`, we're more granular and instead
        subscribe to the individual events, and handle loading on mount ourselves. */
    return (
      <div className="alert-list">
        <ReactTable
          className="alert-table"
          minRows={0}
          columns={tableColumnSpec}
          data={data}
          page={this.props.currentPage}
          pageSize={this.props.pageSize}
          pages={this.props.numPages}
          onPageChange={this.props.setPage}
          onPageSizeChange={this.props.setPageSize}
          onSortedChange={this.handleSortChange}
          LoadingComponent={TableLoader}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          loading={this.isLoading}
          NoDataComponent={this.NoDataComponent}
          column={{
            ...ReactTableDefaults.column,
            className: 'table-cell',
          }}
          manual={true}
          //   filterable={true}
          getTdProps={this.getTdProps}
          onFilteredChange={this.props.handleTableFiltersChanged}
        />
      </div>
    );
  }

  private NoDataComponent(): JSX.Element {
    return <NoDataMessage isLoading={this.isLoading} />;
  }

  private get isLoading(): boolean {
    return this.props.isLoading;
  }

  private getTdProps(state: any, rowInfo: any, column: any, instance: any): {} {
    return {
      onClick: (e: any, handleOriginal: undefined | null | (() => void)) => {
        if (rowInfo && rowInfo.original instanceof ProductAlert) {
          this.props.showAlert(rowInfo.original as ProductAlert);
        }
        if (handleOriginal) {
          handleOriginal();
        }
      },
    };
  }

  /**
   * Handles sorting change events provided by the React Table component.
   * @param newSorted The new array denoting how columns should be sorted. This array
   * should have all current sort rules, and should not just be the change that occurred.
   * @param column The colummn object to which sorting was applied.
   * @param additive True if the user is adding to previous sorting rules, false otherwise.
   * Users can perform multi-sort by holding shift.
   */
  private handleSortChange(
    newSorted: SortingRule[],
    column: any,
    additive: boolean
  ): void {
    const sortRules: AlertSortSpecification = new Map({
      *[Symbol.iterator]() {
        for (const { id, desc } of newSorted) {
          if (alertSortAllowedKeys.has(id)) {
            /* Although the sort rules from React-Table are typed with additional 'asc' and 'sort'
                        keys, they don't seem to actually be used. This may change in future versions,
                        though the current React-Table is completely different anyway. */
            const sortDirection: 'asc' | 'desc' = desc ? 'desc' : 'asc';
            yield [id as AlertSortAllowedKey, sortDirection];
          } else {
            console.error('Encountered unhandled sort ID in AlertList: ', id);
          }
        }
      },
    });

    this.props.setSortSpecification(sortRules);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/Alerts/AlertList.tsx